import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Styled from 'styled-components';
import { Modal } from '../../common/modals/antd-modals';

const YOUTUBE_ID_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
const getYoutubeId = (url) => {
  if (!url) return null;
  const match = String(url).match(YOUTUBE_ID_RE);
  return match ? match[1] : null;
};

const imgUrl = (img) =>
  img?.large || img?.gallerythumb || img?.url || img?.thumbnail || img?.image || img?.imageURL || img?.sizes?.full || img?.localPreview || null;

/* Figma modal box: max 964 x 628 (width set on the Modal); cap the height here. */
const Body = Styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 13px;
  padding: 24px 18px 24px 24px;
  background: #fff;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: min(628px, 88vh);
`;

/* Mobile: the gallery opens as a bottom sheet (Figma) with a "Listing Media" header. */
const SheetOverlay = Styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  align-items: flex-end;
`;

const Sheet = Styled.div`
  background: #fff;
  border-radius: 16px 16px 0 0;
  width: 100%;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SheetHeader = Styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  flex-shrink: 0;

  .title { color: #222; font-size: 16px; font-weight: 700; }
  button { background: none; border: 0; cursor: pointer; padding: 4px; line-height: 0; }
`;

const SheetBody = Styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 13px;
  padding: 0 16px 16px;
`;

const CloseBtn = Styled.button`
  position: absolute;
  top: 20px;
  inset-inline-end: 18px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  z-index: 2;
`;

const Tabs = Styled.div`
  display: flex;
  align-items: stretch;
  gap: 1px;
  padding: 5px;
  width: 100%;
  max-width: 733px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #dedede;
`;

const Tab = Styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 28px;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  white-space: nowrap;
  background: ${({ $active }) => ($active ? '#F2FAFA' : 'transparent')};
  color: ${({ $active }) => ($active ? '#006169' : '#707070')};

  svg { flex-shrink: 0; }
`;

const Stage = Styled.div`
  position: relative;
  width: 100%;
  max-width: 922px;
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f5;

  img.main {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-frame {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }

  .empty {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9d9d9d;
    font-size: 15px;
  }
`;

const StageCounter = Styled.span`
  position: absolute;
  top: 12px;
  inset-inline-end: 12px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  padding: 3px 10px;
  line-height: 1.5;
  z-index: 3;
  pointer-events: none;
`;

const NavArrow = Styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  /* Physical left/right (not logical inset-inline-*) so the prev/next arrows keep the same
     fixed positions and directions in Arabic (RTL) as in English — no side-swap. */
  ${({ $side }) => ($side === 'left' ? 'left: 16px;' : 'right: 16px;')}
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $side }) => ($side === 'left' ? '#F5F5F5' : '#FFFFFF')};
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
  z-index: 2;

  &:disabled { opacity: 0.4; cursor: default; }
`;

const ImagesTabIcon = ({ color }) => (
  <svg width="18" height="15" viewBox="0 0 18 15" fill="none" aria-hidden="true">
    <path
      d="M1.35 14.4C0.99 14.4 0.675 14.265 0.405 13.995C0.135 13.725 0 13.41 0 13.05V1.35C0 0.99 0.135 0.675 0.405 0.405C0.675 0.135 0.99 0 1.35 0H16.65C17.01 0 17.325 0.135 17.595 0.405C17.865 0.675 18 0.99 18 1.35V13.05C18 13.41 17.865 13.725 17.595 13.995C17.325 14.265 17.01 14.4 16.65 14.4H1.35ZM1.35 13.05H16.65V1.35H1.35V13.05ZM3.528 10.8H14.49L11.187 6.327L8.235 10.233L6.165 7.335L3.537 10.8H3.528Z"
      fill={color}
    />
  </svg>
);

const VideosTabIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M9 1.5C10.4834 1.5 11.9334 1.93987 13.1668 2.76398C14.4001 3.58809 15.3614 4.75943 15.9291 6.12987C16.4968 7.50032 16.6453 9.00832 16.3559 10.4632C16.0665 11.918 15.3522 13.2544 14.3033 14.3033C13.2544 15.3522 11.918 16.0665 10.4632 16.3559C9.00832 16.6453 7.50032 16.4968 6.12988 15.9291C4.75943 15.3614 3.58809 14.4001 2.76398 13.1668C1.93987 11.9334 1.5 10.4834 1.5 9C1.5 7.01088 2.29018 5.10322 3.6967 3.6967C5.10323 2.29018 7.01088 1.5 9 1.5ZM9 0C7.21997 0 5.47991 0.527841 3.99987 1.51677C2.51983 2.50571 1.36628 3.91131 0.685088 5.55585C0.00389957 7.20038 -0.17433 9.00998 0.172937 10.7558C0.520203 12.5016 1.37737 14.1053 2.63604 15.364C3.89472 16.6226 5.49836 17.4798 7.24419 17.8271C8.99002 18.1743 10.7996 17.9961 12.4442 17.3149C14.0887 16.6337 15.4943 15.4802 16.4832 14.0001C17.4722 12.5201 18 10.78 18 9C18 6.61305 17.0518 4.32387 15.364 2.63604C13.6761 0.948212 11.387 0 9 0Z"
      fill={color}
    />
    <path d="M13.5 9L6.75 12.9V5.1L13.5 9Z" fill={color} />
  </svg>
);

const Chevron = ({ left }) => (
  <svg width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden="true">
    <path
      d={
        left
          ? 'M3.638 8.995 9.564 2.463a1.36 1.36 0 0 0-.561-2.42 1.36 1.36 0 0 0-1.262.41L0 8.986l1.987 2.19 5.779 6.371a1.36 1.36 0 0 0 1.997-1.84L3.638 8.995Z'
          : 'M6.362 8.995.436 2.463A1.36 1.36 0 0 1 .997.043a1.36 1.36 0 0 1 1.263.41L10 8.986l-1.987 2.19-5.779 6.371a1.36 1.36 0 0 1-1.997-1.84L6.362 8.995Z'
      }
      fill="#222222"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M0.45 0.45 13.18 13.18M13.18 0.45 0.45 13.18" stroke="#222222" strokeWidth="1.27" />
  </svg>
);

/**
 * Listing media gallery (LP-5096 Figma) — Images / Videos tabbed viewer.
 * Images: carousel of property photos. Videos: embedded YouTube players.
 */
const MediaGalleryModal = ({ visible, onClose, images = [], videos = [], initialTab = 'images', initialIndex = 0 }) => {
  const { t } = useTranslation();
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  const usableImages = useMemo(
    () => (images || []).filter((img) => !img?.inError && !img?._destroy && imgUrl(img)),
    [images],
  );
  const usableVideos = useMemo(
    () => (videos || []).filter((v) => !v?._destroy && getYoutubeId(v?.url || v?.link)).map((v) => getYoutubeId(v.url || v.link)),
    [videos],
  );

  const [tab, setTab] = useState(initialTab);
  const [imgIdx, setImgIdx] = useState(0);
  const [vidIdx, setVidIdx] = useState(0);

  useEffect(() => {
    if (visible) {
      const openVideos = initialTab === 'videos' && usableVideos.length;
      setTab(openVideos ? 'videos' : 'images');
      setImgIdx(openVideos ? 0 : initialIndex);
      setVidIdx(openVideos ? initialIndex : 0);
    }
  }, [visible, initialTab, initialIndex]);

  const isVideos = tab === 'videos';

  const galleryContent = (
    <>
        <Tabs>
          <Tab $active={!isVideos} type="button" onClick={() => setTab('images')}>
            <ImagesTabIcon color={!isVideos ? '#006169' : '#707070'} />
            {t('Images')} ({usableImages.length})
          </Tab>
          <Tab $active={isVideos} type="button" onClick={() => setTab('videos')}>
            <VideosTabIcon color={isVideos ? '#006169' : '#707070'} />
            {t('Videos')} ({usableVideos.length})
          </Tab>
        </Tabs>

        <Stage>
          {isVideos ? (
            usableVideos.length ? (
              <>
                <iframe
                  className="video-frame"
                  src={`https://www.youtube.com/embed/${usableVideos[vidIdx]}`}
                  title={`video-${vidIdx}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
                <StageCounter>{vidIdx + 1}/{usableVideos.length}</StageCounter>
                {usableVideos.length > 1 && (
                  <>
                    <NavArrow
                      $side="left"
                      type="button"
                      disabled={vidIdx === 0}
                      onClick={() => setVidIdx((i) => Math.max(0, i - 1))}
                    >
                      <Chevron left />
                    </NavArrow>
                    <NavArrow
                      $side="right"
                      type="button"
                      disabled={vidIdx === usableVideos.length - 1}
                      onClick={() => setVidIdx((i) => Math.min(usableVideos.length - 1, i + 1))}
                    >
                      <Chevron />
                    </NavArrow>
                  </>
                )}
              </>
            ) : (
              <div className="empty">{t('No videos added yet')}</div>
            )
          ) : usableImages.length ? (
            <>
              <img className="main" src={imgUrl(usableImages[imgIdx])} alt={`image-${imgIdx}`} />
              <StageCounter>{imgIdx + 1}/{usableImages.length}</StageCounter>
              {usableImages.length > 1 && (
                <>
                  <NavArrow
                    $side="left"
                    type="button"
                    onClick={() => setImgIdx((i) => (i - 1 + usableImages.length) % usableImages.length)}
                  >
                    <Chevron left />
                  </NavArrow>
                  <NavArrow
                    $side="right"
                    type="button"
                    onClick={() => setImgIdx((i) => (i + 1) % usableImages.length)}
                  >
                    <Chevron />
                  </NavArrow>
                </>
              )}
            </>
          ) : (
            <div className="empty">{t('No images added yet')}</div>
          )}
        </Stage>
    </>
  );

  if (isMobile) {
    if (!visible) return null;
    return (
      <SheetOverlay onClick={onClose}>
        <Sheet onClick={(e) => e.stopPropagation()}>
          <SheetHeader>
            <span className="title">{t('Listing Media')}</span>
            <button type="button" onClick={onClose} aria-label={t('Close')}>
              <CloseIcon />
            </button>
          </SheetHeader>
          <SheetBody>{galleryContent}</SheetBody>
        </Sheet>
      </SheetOverlay>
    );
  }

  return (
    <Modal visible={visible} onCancel={onClose} footer={null} hideCloseIcon width={964} bodyStyle={{ padding: 0 }}>
      <Body>
        <CloseBtn type="button" onClick={onClose} aria-label={t('Close')}>
          <CloseIcon />
        </CloseBtn>
        {galleryContent}
      </Body>
    </Modal>
  );
};

export default MediaGalleryModal;
